import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { assemblyStatusCodes } from "@/pages/mill/carriers/components/CarrierAssemblyStatusTag";
import { ConfirmPassDeletionModal } from "@/pages/mill/carriers/components/passes/ConfirmPassDeletionModal";
import type {
	AffectedPassChange,
	PassChangeSummary,
} from "@/pages/mill/carriers/repositories/CarrierAssemblyRepository";
import {
	previewPassChangesOutsideWindow,
	updateCarrierStatus,
} from "@/pages/mill/carriers/repositories/CarrierAssemblyRepository";
import { routeCarrierAssembly } from "@/setup/routes/mill.routes";
import {
	ErrorModal,
	getMomentTz,
	MesfModal,
	TimeService,
} from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Grid2,
	IconButton,
	Link,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { StandSpare } from "../../models/StandSpare";
import { getMillDetailsByStandId } from "../../repositories/MillOverviewRepository";

const moment = getMomentTz();

type useMillDetailsByStandIdProps = {
	standId: number;
	show: boolean;
	onError: (error: Error) => void;
};

export const useMillDetailsByStandId = ({
	standId,
	show,
	onError,
}: useMillDetailsByStandIdProps) => {
	return useQuery<StandSpare[], Error>({
		queryKey: ["millDetails", standId, show],
		meta: { realtime: ["M2.GetMillDetailsByStandId"] },
		queryFn: ({ signal }) => {
			if (show) {
				return getMillDetailsByStandId({ standId }, signal);
			} else {
				return [];
			}
		},
		onError: onError,
	});
};

export const InstallUninstallModal = ({
	show,
	onHide,
	standId,
	standName,
}: {
	show: boolean;
	onHide: (success: boolean) => void;
	standId: number;
	standName: string;
}) => {
	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [selectedCarrier, setSelectedCarrier] = useState<StandSpare | null>(
		null,
	);
	const [spares, setSpares] = useState<StandSpare[] | null>(null);
	const [uninstallTimestamp, setUninstallTimestamp] = useState<Date | null>(
		null,
	);

	// Pass deletion confirmation state
	const [showConfirmDeletion, setShowConfirmDeletion] = useState(false);
	const [affectedPasses, setAffectedPasses] = useState<AffectedPassChange[]>(
		[],
	);
	const [passSummary, setPassSummary] = useState<PassChangeSummary[]>([]);

	const isUninstall = selectedCarrier?.CarrierStatusCode === "I";

	const {
		data: carriers,
		isLoading,
		isSuccess,
	} = useMillDetailsByStandId({
		standId: standId,
		show: show,
		onError: (error) => {
			setError(error.message);
		},
	});

	const queryClient = useQueryClient();
	const updateStatus = useMutation(updateCarrierStatus, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["millOverviews"]);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const executeUninstall = (forceDeletePasses: boolean) => {
		setIsSubmitLoading(true);
		updateStatus.mutate({
			partId: selectedCarrier?.PartId!,
			newStatus: "U",
			timestamp: uninstallTimestamp,
			forceDeletePasses,
		});
	};

	const onSubmit = async () => {
		if (!isUninstall) {
			// Install flow - no changes needed
			setIsSubmitLoading(true);
			updateStatus.mutate({
				partId: selectedCarrier?.PartId!,
				newStatus: "I",
			});
			return;
		}

		// Uninstall flow - check for affected pass changes
		const timestamp = uninstallTimestamp || new Date();
		if (!selectedCarrier?.CarrierInstanceId) {
			executeUninstall(false);
			return;
		}

		try {
			setIsSubmitLoading(true);
			const preview = await previewPassChangesOutsideWindow({
				carrierInstanceId: selectedCarrier.CarrierInstanceId,
				proposedUninstalledAt: timestamp,
			});

			if (preview.affectedPasses.length > 0) {
				setAffectedPasses(preview.affectedPasses);
				setPassSummary(preview.summary);
				setShowConfirmDeletion(true);
				setIsSubmitLoading(false);
			} else {
				executeUninstall(false);
			}
		} catch (err: any) {
			setError(err.message || "Error checking pass changes");
			setIsSubmitLoading(false);
		}
	};

	const handleConfirmDeletion = () => {
		setShowConfirmDeletion(false);
		executeUninstall(true);
	};

	useEffect(() => {
		if (isSuccess) {
			setSelectedCarrier(
				carriers.find((c) => c.CarrierStatusCode === "I") || null,
			);
			setSpares(carriers.filter((c) => c.CarrierStatusCode !== "I"));
		}
	}, [isSuccess, carriers]);

	useEffect(() => {
		if (show) {
			setUninstallTimestamp(new Date());
		}
	}, [show]);

	return (
		<>
			<MesfModal
				maxWidth="xl"
				open={show}
				handleClose={() => {
					onHide(false);
				}}
				id="install_uninstall_modal"
				title={`${
					selectedCarrier?.CarrierStatusCode === "I" ? "Uninstall" : "Install"
				} for stand: ${standName}`}
			>
				<MesfModal.Content style={{ padding: "20px 30px" }} dividers>
					{isLoading && <CenteredLazyLoading />}
					{!isLoading && (
						<Box style={{ width: "600px" }}>
							{selectedCarrier?.CarrierStatusCode == "I" ? (
								<>
									<CarrierCard carrier={selectedCarrier} />
									<Box sx={{ marginTop: 2 }}>
										<DateTimePicker
											label="Uninstall Timestamp"
											value={
												uninstallTimestamp ? moment(uninstallTimestamp) : null
											}
											onChange={(date) =>
												setUninstallTimestamp(date?.toDate() ?? null)
											}
											timezone={TimeService.getInstance().getServerTimeZone()}
											format="MM/DD/YYYY HH:mm:ss"
											ampm={false}
											maxDateTime={moment()}
											slotProps={{
												textField: {
													size: "small",
													fullWidth: true,
												},
											}}
										/>
									</Box>
								</>
							) : (
								<Box
									sx={{
										marginTop: (theme) => theme.spacing(2),
										maxHeight: "50vh",
										overflowY: "auto",
									}}
								>
									{carriers && carriers[0]?.CarrierStatusCode == null && (
										<Box style={{ textAlign: "center" }}>
											<Typography>
												There are no carriers to install for the stand{" "}
												{standName}. You have to go to carrier assembly and make
												a new build
											</Typography>
										</Box>
									)}

									{spares &&
										spares.length > 0 &&
										spares.map(
											(spare, index) =>
												spare.PartId && (
													<>
														<Box style={{ position: "relative" }}>
															<CarrierCard
																carrier={spare}
																selectedCarrier={selectedCarrier}
																setSelectedCarrier={setSelectedCarrier}
															/>
															{selectedCarrier?.PartId == spare.PartId && (
																<IconButton
																	style={{
																		position: "absolute",
																		top: -6,
																		right: 0,
																		cursor: "pointer",
																		backgroundColor: "white",
																		borderRadius: "50%",
																		width: 30,
																		height: 30,
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		zIndex: 1,
																	}}
																	onClick={(e) => {
																		setSelectedCarrier?.(null);
																	}}
																	title="Clear selection"
																>
																	<DeleteIcon
																		style={{ fontSize: 20 }}
																		color="secondary"
																	/>
																</IconButton>
															)}
														</Box>
													</>
												),
										)}
								</Box>
							)}
						</Box>
					)}
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 20px" }}>
					<Grid2 container spacing={1} alignItems="center">
						<Grid2 size={{ md: 4 }}>
							{(!carriers || carriers[0]?.CarrierStatusCode == null) && (
								<Link href={routeCarrierAssembly} target="_blank">
									<Button variant="outlined" color="primary">
										Go to carriers
									</Button>
								</Link>
							)}
						</Grid2>
						<Grid2
							size={{ md: 8 }}
							display="flex"
							justifyContent="flex-end"
							gap={1}
						>
							<Button
								variant="contained"
								color="inherit"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>

							<Button
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								disabled={isSubmitLoading || !selectedCarrier}
								variant="contained"
								color={
									selectedCarrier?.CarrierStatusCode === "I"
										? "secondary"
										: "primary"
								}
								onClick={onSubmit}
							>
								{selectedCarrier?.CarrierStatusCode === "I"
									? "Uninstall"
									: "Install"}
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ConfirmPassDeletionModal
				open={showConfirmDeletion}
				onClose={() => setShowConfirmDeletion(false)}
				onConfirm={handleConfirmDeletion}
				affectedPasses={affectedPasses}
				summary={passSummary}
			/>
		</>
	);
};

const CarrierCard = ({
	carrier,
	selectedCarrier,
	setSelectedCarrier,
}: {
	carrier: StandSpare;
	selectedCarrier?: StandSpare | null;
	setSelectedCarrier?: (carrier: StandSpare | null) => void;
}) => {
	const isSelected = selectedCarrier?.PartId === carrier.PartId;
	const isInstalled = carrier.CarrierStatusCode === "I";

	return (
		<Card
			sx={{
				borderRadius: "8px",
				padding: (theme) => theme.spacing(2),
				marginBottom: (theme) => theme.spacing(1),
				background: assemblyStatusCodes.find(
					(a) => a.Code == carrier.CarrierStatusCode,
				)?.Color,
				border: isSelected ? "3px solid white" : "none",
				cursor:
					isInstalled || carrier.CarrierStatusCode === "S"
						? "pointer"
						: "default",
			}}
			elevation={1}
			onClick={() => {
				if (setSelectedCarrier) {
					if (isInstalled || carrier.CarrierStatusCode === "S") {
						setSelectedCarrier(carrier);
					}
				}
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: (theme) => theme.spacing(1),
				}}
			>
				<Typography fontWeight={"bold"} fontSize={"1rem"} color={"white"}>
					{carrier.PartName}
				</Typography>
				<Box
					sx={{
						fontSize: "0.9rem",
						fontWeight: "bold",
						borderRadius: "12px",
						color: "white",
					}}
				>
					{
						assemblyStatusCodes.find((a) => a.Code == carrier.CarrierStatusCode)
							?.Name
					}
				</Box>
			</Box>
			<Box
				sx={{
					fontSize: "0.8rem",
					color: "white",
				}}
				display="flex"
				justifyContent="space-between"
			>
				<Grid2>
					<Typography
						fontSize={"0.875rem"}
						sx={{
							opacity: 0.9,
							marginBottom: (theme) => theme.spacing(0.5),
						}}
					>
						Section
					</Typography>
					<Typography
						fontSize={"0.875rem"}
						fontWeight={"bold"}
						textAlign={"center"}
					>
						{carrier?.SectionName}
					</Typography>
				</Grid2>
				<Grid2>
					<Typography
						fontSize={"0.875rem"}
						sx={{
							opacity: 0.9,
							marginBottom: (theme) => theme.spacing(0.5),
						}}
					>
						Top Roll
					</Typography>
					<Typography
						fontSize={"0.875rem"}
						fontWeight={"bold"}
						sx={{
							textAlign: "center",
						}}
					>
						{carrier?.TopRollName}
					</Typography>
				</Grid2>
				<Grid2>
					<Typography
						fontSize={"0.875rem"}
						sx={{
							opacity: 0.9,
							marginBottom: (theme) => theme.spacing(0.5),
						}}
					>
						Bottom Roll
					</Typography>
					<Typography
						fontSize={"0.875rem"}
						fontWeight={"bold"}
						sx={{
							textAlign: "center",
						}}
					>
						{carrier?.BottomRollName}
					</Typography>
				</Grid2>
				<Grid2>
					<Typography
						fontSize={"0.875rem"}
						sx={{
							opacity: 0.9,
							marginBottom: (theme) => theme.spacing(0.5),
						}}
					>
						Diameter
					</Typography>
					<Typography
						fontSize={"0.875rem"}
						fontWeight={"bold"}
						sx={{
							textAlign: "center",
						}}
					>
						{carrier?.TopRollDiameter?.toFixed(3)}
					</Typography>
				</Grid2>
			</Box>
		</Card>
	);
};
